import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Folder } from '@api/types';
import { FolderReferenceService } from '@shared/services/folder-reference.service';
import { ImportStatusService } from '@shared/services/import-status.service';
import { getMimeTypeFromExtension } from '@shared/utils/file-type';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Subscription } from 'rxjs';
import { ImportJob, ImportType, TreeNode } from '../types/import';

@Component({
  selector: 'rav-import-folder',
  templateUrl: './import-folder.component.html',
  styleUrls: ['./import-folder.component.scss']
})
export class ImportFolderComponent implements OnInit, OnDestroy {
  folderButtonTouched: boolean;
  jobs: ImportJob[] = [];
  inbox: Folder;
  topFolder: Folder;
  currentJobIndex = 0;
  readonly FILE_MAX_SIZE: number = 30000000;
  readonly MAX_REQUESTS: number = 10;
  readonly VALID_FILE_TYPE: string[] = ['image/tiff', 'image/jpg', 'image/jpeg', 'application/pdf'];
  subscriptions: Subscription[] = [];
  uploading: number;
  complete: number;
  fullPath: string[] = [];
  isFirefox = false;

  constructor(
    private deviceService: DeviceDetectorService,
    private folderReferenceService: FolderReferenceService,
    private importStatusService: ImportStatusService,
    private router: Router
  ) {}

  async ngOnInit() {
    this.isFirefox = this.deviceService.getDeviceInfo().browser === 'Firefox';
    this.topFolder = await this.folderReferenceService.getTopFolder();
    this.inbox = await this.folderReferenceService.getInbox();

    this.subscriptions.push(
      this.importStatusService.dragImportAnnounced$.subscribe(async (importObject) => {
        const { type, files, selectedFolder, fullPath } = importObject;
        if (type === ImportType.Folder) {
          const job = await this.createJob(files, selectedFolder, fullPath);
          if (job) {
            this.importStatusService.createNewJobs([job], ImportType.Folder);
          }
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Handler whenever user selects a folder
   *
   * @param files Selected files
   */
  async filesSelected(files: File[]) {
    const currentFolder = await this.getCurrentFolder();
    const job = await this.createJob(files, currentFolder);
    if (job) {
      this.importStatusService.createNewJobs([job], ImportType.Folder);
    }
  }

  /**
   * Gets current folder according to Url
   *
   * @returns folder name string
   */
  async getCurrentFolder() {
    const urlPath = this.router.url.includes('?')
      ? this.router.url.substring(0, this.router.url.indexOf('?')).split('/')
      : this.router.url.split('/');

    if (this.topFolder.title.toLowerCase() === urlPath[1]) {
      const currentFolderName = urlPath[2] ? await this.folderReferenceService.getFolderName(urlPath[2]) : undefined;

      return currentFolderName ? new TreeNode(urlPath[2], currentFolderName, [], null, null, false) : undefined;
    }
    return undefined;
  }

  /**
   * Whenever a user selects new folder clean up the old one here
   *
   * @param event Event
   */
  cleanCache(event: any) {
    event.target.value = null;
  }

  /**
   * Iterate selected files to get valid files
   *
   * @param files Selected files in the folder
   * @param root The root of the trees
   *
   * @returns array with only the valid Files to import
   */
  private async parseFiles(files: File[], root: TreeNode, fullPath?: string[]): Promise<File[]> {
    const validFiles = [];
    for (let i = 0; i < files.length; i++) {
      // Need to cut off the forward slash
      if (fullPath && fullPath[i] && fullPath[i].charAt(0) === '/') {
        fullPath[i] = fullPath[i].substr(1);
      }
      const file = files[i];
      if (this.validateFile(file)) {
        validFiles.push(file);
        await this.preprocessing(file, root, true, fullPath[i]);
      } else {
        await this.preprocessing(file, root, false, fullPath[i]);
      }
    }
    return validFiles;
  }

  /**
   * Initialize the folder tree structure according to the file path
   *
   * @param file uploaded file
   */
  private async preprocessing(file: any, root: TreeNode, isValid: boolean, fullPath?: string) {
    const path = file.webkitRelativePath || fullPath;
    const pathItems = path.split('/');
    let currentNode = root;

    for (let i = 0; i < pathItems.length - 1; i++) {
      const folder = currentNode.children.find((child) => child.title === pathItems[i]);

      if (folder) {
        currentNode = folder;
      } else {
        const node = new TreeNode(null, pathItems[i], [], currentNode, null, isValid);
        currentNode.children.push(node);
        currentNode = node;
      }
    }

    currentNode.children.push(new TreeNode(null, pathItems[pathItems.length - 1], [], currentNode, file, isValid));

    // If current uploading job is a single file
    if (root.children.length === 1 && root.children[0].file !== null) {
      root.id = this.inbox.id;
      root.title = this.inbox.title;
    }
  }

  /**
   * Validate if single file is valid by checking its type and size
   *
   * @param file Input file
   */
  private validateFile(file: File): boolean {
    if (this.isFirefox) {
      const fileType = this.getFileType(file);
      return file.size <= this.FILE_MAX_SIZE && file.size > 0 && this.VALID_FILE_TYPE.includes(fileType);
    } else {
      return file.size <= this.FILE_MAX_SIZE && file.size > 0 && file.type && this.VALID_FILE_TYPE.includes(file.type);
    }
  }

  /**
   * Workaround for firefox because the type property of the File object converted from file entry is empty on firefox,
   * so need to set file type using file extension.
   *
   * @param file The File object
   */
  private getFileType(file: File): string {
    return getMimeTypeFromExtension(file.name.toLowerCase().substring(file.name.lastIndexOf('.')));
  }

  /**
   * Create a instance of Job object.
   *
   * @param files the files need to be uploaded.
   * @param targetFolderNode the folder that need to be uploaded files to.
   * @param fullPath the full path of files from file explorer.
   */
  private async createJob(files: File[], targetFolderNode?, fullPath?: string[]): Promise<ImportJob | null> {
    try {
      let shouldContinue = true;
      const root = targetFolderNode
        ? new TreeNode(targetFolderNode.id, targetFolderNode.name, [], null, null, true)
        : new TreeNode(this.topFolder.id, 'Folders', [], null, null, true);

      if (files.length === 0 || (fullPath && fullPath.length === 0)) {
        alert('Sorry, we can not import an empty folder.');
        return;
      }

      const validFiles = await this.parseFiles(files, root, fullPath || []);

      // Only show confirm window once when importing multiple folders.
      if (validFiles.length < files.length) {
        const folderName = root.children[0].title;
        shouldContinue = confirm(
          `We only support PDF, JPEG, JPG or TIFF documents up to 30MB per file. Other types of files will not be imported.

Do you still want to import ${folderName} ?`
        );
        if (shouldContinue && validFiles.length === 0) {
          alert(`Sorry, we can not import ${folderName} with no valid files.`);
        }
      }

      if (validFiles.length > 0 && shouldContinue) {
        // If current job is a single file then we create different object to display the file upload process(Only happen in Safari)
        const job =
          root.id === this.inbox.id && validFiles.length === 1
            ? new ImportJob(
                root,
                '',
                validFiles.length,
                validFiles[0].name.substring(0, validFiles[0].name.lastIndexOf('.'))
              )
            : new ImportJob(root, root.children[0].title, validFiles.length);
        return job;
      }
      return null;
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/quotes
      alert("There's been an error importing your folder");
    }
  }
}
